import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { SortingType } from 'src/app/enums/sorting-type';
import { CustomAction } from '../../types/table.type';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { VmStatus } from 'src/app/interfaces/vm.interface';
import { VMStateLabels } from 'src/app/Util/constants';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.sass']
})
export class TableComponent<T> implements OnInit, AfterViewInit {

  @Input() columnNames: string[] = []
  @Input() data: T[]
  @Input() showPagination: boolean = false
  @Input() showSorting: boolean = false
  @Input() showCheckBoxes: boolean = false
  @Input() itemsPerPage: number = 5
  @Input() actions: Array<CustomAction<T>> = []
  @Input() columnRenderers: Map<number, (data: T) => string> = new Map()
  @Input() ignoreColumns: string[] = []
  @Output() setSelectedItems = new EventEmitter()

  objectKeys: string[]

  //variables for pagination
  totalItems: number
  numOfPages: number
  currentPage: number = 1
  currentItems: string
  listOfPages: number[] = []
  listOfItems: T[]

  //variables for sorting
  sortProperty: string = ""
  sortType: SortingType = SortingType.None

  //variables for checkbox
  selectedItems: T[] = []
  allChecked: boolean = false

  // variables for column data override
  columnDataOverridden: boolean[] = []
  overriddenData: [SafeHtml[]] = [[]];

  constructor(private safeHtml: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {

    this.objectKeys = Object.keys(this.data[0])
    this.listOfItems = [...this.data]

    if (this.columnNames.length == 0 && this.data.length > 0) {
      this.objectKeys.forEach((key) => {
        if (this.ignoreColumns.includes(key)) return;

        this.columnNames.push(key.charAt(0).toUpperCase() + key.slice(1))
      })
    }

    if (this.showPagination) {
      this.totalItems = this.data.length
      this.numOfPages = Math.ceil(this.totalItems / this.itemsPerPage)
      this.setPageList()
      this.setIemList()
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.data && !(changes?.data.isFirstChange())) {
      this.data = [...changes.data.currentValue];
      this.listOfItems = this.data;
      this.overriddenData = [[]];
      this.overrideData();
    }
  }

  ngAfterViewInit(): void {
    for (let i = 0; i < this.columnNames.length; i++)
      this.columnDataOverridden.push(this.validateRenderer(i))
    this.columnDataOverridden = [...this.columnDataOverridden];
    if (this.columnDataOverridden.includes(true)) {
      this.overrideData();
    }
    this.cdr.detectChanges();
  }

  overrideData() {
    this.overriddenData.pop();
    this.data.forEach((item) => {
      let overriddenData: SafeHtml[] = new Array(this.columnNames.length);
      for (let i = 0; i < this.columnNames.length; i++) {
        var html = ""
        if (this.columnDataOverridden[i]) {
          html = this.columnRenderers.get(i)(item[this.objectKeys[i]]);
          overriddenData[i] = this.safeHtml.bypassSecurityTrustHtml(html)
        }
      }
      this.overriddenData.push(overriddenData)
    })
  }

  setIemList() {
    this.sortProperty = ""
    this.sortType = SortingType.None

    let numOfItems = this.currentPage * this.itemsPerPage
    let startItem = (numOfItems - this.itemsPerPage) + 1
    let endItem = (numOfItems > this.totalItems) ? this.totalItems : numOfItems
    this.currentItems = "" + startItem + "-" + endItem

    if (startItem == endItem) {
      this.listOfItems = this.data.slice(startItem - 1)
    }
    else {
      this.listOfItems = this.data.slice(startItem - 1, endItem)
    }
  }

  setPageList() {
    this.listOfPages = []
    let difference = (this.currentPage == this.numOfPages) ? 2 : 1
    let startIndex = (this.currentPage - 1) <= 1 ? 2 : this.currentPage - difference
    difference = (this.currentPage == 1 || this.numOfPages <= 5) ? 2 : 1
    let endIndex = (this.currentPage + difference) >= this.numOfPages ? this.numOfPages - 1 : this.currentPage + difference
    for (let i = startIndex; i <= endIndex; i++) {
      this.listOfPages.push(i)
    }
  }

  goToNext() {
    this.currentPage += 1
    this.setIemList()
    this.setPageList()
  }

  goToPrevious() {
    this.currentPage -= 1
    this.setIemList()
    this.setPageList()
  }

  goToPage(pageNum: number) {
    this.currentPage = pageNum
    this.setIemList()
    this.setPageList()
  }

  sortAscending(index) {
    let property = this.objectKeys[index]
    if (property == this.sortProperty && this.sortType == SortingType.Ascending) {
      if (this.showPagination) {
        this.setIemList()
      }
      else {
        this.listOfItems = [...this.data]
        this.sortProperty = ""
        this.sortType = SortingType.None
      }
    }
    else {
      this.sortType = SortingType.Ascending
      this.sortProperty = property
      if (typeof this.listOfItems[0][property] == "string") {
        this.listOfItems.sort((a, b) => {
          let first = a[property]
          let second = b[property]
          if (first < second) return -1
          if (first > second) return 1
          return 0
        })
      }
    }
  }

  sortDescending(index) {
    let property = this.objectKeys[index]
    if (property == this.sortProperty && this.sortType == SortingType.Descending) {
      if (this.showPagination) {
        this.setIemList()
      }
      else {
        this.listOfItems = [...this.data]
        this.sortProperty = ""
        this.sortType = SortingType.None
      }
    }
    else {
      this.sortType = SortingType.Descending
      this.sortProperty = property
      if (typeof this.listOfItems[0][property] == "string") {
        this.listOfItems.sort((a, b) => {
          let first = a[property]
          let second = b[property]
          if (first < second) return 1
          if (first > second) return -1
          return 0
        })
      }
    }
  }

  setSelectedItem(item: T) {
    if (this.selectedItems.includes(item)) {
      this.selectedItems = this.selectedItems.filter((selectedItem) => {
        if (selectedItem != item) {
          return selectedItem
        }
      })
      this.allChecked = false
    }
    else {
      this.selectedItems.push(item)
    }
    this.setSelectedItems.emit(this.selectedItems)
  }

  selectAllItems() {
    if (this.allChecked) {
      this.allChecked = false
      this.selectedItems = []
    }
    else {
      this.allChecked = true
      this.selectedItems = [...this.data]

    }
    this.setSelectedItems.emit(this.selectedItems)
  }

  validateRenderer(index: number): boolean {
    return this.columnRenderers.has(index)
  }

  ignoreColumn(objectKey: string) {
    return !this.ignoreColumns
      .includes(
        this.columnNames[this.objectKeys.findIndex((x) => x === objectKey)]
      );
  }
}

export class TableUtil {
  /**
   * @description This method is used to get HTML content for the table cell. It generates an fas-circle icon with the color based on the status of the VM.
   * @param statuses gets the status of the VMs
   * @returns string
   */
  public static generateListWithColorableIcons(statuses: MachineStatuses): string {
    let list = "";
    statuses.forEach((status, ip) => {
      if (status.status.toLowerCase() === "loading") {

        list += `<i class="fa fa-spinner fa-spin me-2" style="color: #3498DB; vertical-align: middle; line-height: 1;"></i> ${ip} <br>`;


      } else {
        const color = TableUtil.getColor(status.status);
        list += `<i class="fas fa-circle me-2" style="color: ${color};"></i> ${ip} <br>`;
      }
    });
    return list;
  }

  public static generateStatusListWIthColorableIcons(statuses: MachineStatuses): string {
    let list = "";
    Object.values(statuses).forEach((status) => {
      if (status.status.toLowerCase() === "loading") {
        list += `<i class="fa fa-spinner fa-spin me-2" style="color: #3498DB; vertical-align: middle; line-height: 1;"></i> ${status.status.charAt(0).toUpperCase()}${status.status.slice(1)} <br>`;
      } else {
        const color = TableUtil.getColor(status.status);
        list += `<i class="fas fa-circle me-2" style="color: ${color};"></i> ${status.status.charAt(0).toUpperCase()}${status.status.slice(1)} <br>`;
      }
    });
  
    return list;
  }


  public static generateText(text: string) {
    if (text) {
      if (text.includes("Marked for removal") || text.includes("Removing")) {
        var name = text.split('(')[0];
        var date = text.split('(')[1];
        return `<p class="mb-0">${name}<span class="text-danger"> (${date}</span></p>`
      }
      return `<p class="mb-0">${text}</p>`
    }
    return `<p></p>`
  }

  /**
   * @description This method is used to get the color based on the status of the VM.
   * @param status gets the status of the VM
   * @returns string
   */
  private static getColor(status: string): string {
    switch (status.toLowerCase()) {
      case VMStateLabels.Running: return "#007bff";
      case VMStateLabels.Stopped: return "lightgray";
      case VMStateLabels.Starting: return "#ffcc00";
      case VMStateLabels.Stopping: return "#ff9900";
      case VMStateLabels.Deallocating: return "#ff6600";
      case VMStateLabels.Deallocated: return "#cccccc";
      case VMStateLabels.Unknown: return "#000000";
      default: return "#000000";
    }
  }

}

/**
 * @description This type is used to define the state of the instance of the VM.
 * Map<string, string> is used to store the key-value pair of the status of the VM.
 * The key is the IP of the VM and the value is the status of the VM.
 */
export type MachineStatuses = Map<string, VmStatus>
